import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Target, Loader2, Play, Pause, Pencil, Ban, ChevronDown, Plus, Trash2, RotateCw, FilterX, Sparkles, Globe, Bookmark, X, SlidersHorizontal, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { PassReasonPicker, passReasonText } from '../components/PassReasonPicker';
import { useAuth } from '../contexts/AuthContext';
import { DealFilterPanel, buildExploreBody, DEFAULT_FILTERS, useFilterOptions, filterSummary } from '../components/DealFilterPanel';
import { CompanyRow } from '../components/ThesisCompanyRow';
import { FundScreenerCard } from '../components/FundScreenerCard';

const FILTER_KEY = 'thesis-filters-v1';


const NumInput = ({ value, onChange, ph, testid }) => (
  <input type="number" value={value} onChange={e => onChange(e.target.value)} placeholder={ph}
    data-testid={testid}
    className="w-16 h-8 bg-secondary border border-border rounded-sm px-2 text-xs text-foreground font-mono focus:outline-none focus:border-violet-500/50" />
);

function ScanCard({ run, stats, onStart, onPause, starting, isAdmin }) {
  const running = run?.status === 'running';
  const pct = stats?.total ? Math.round((stats.scored_current / stats.total) * 100) : 0;
  return (
    <div className="bg-card border border-border rounded-sm p-4 space-y-2.5" data-testid="thesis-scan-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-medium text-foreground">
            KI-Bewertung des Firmen-Universums gegen die These
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="thesis-scan-coverage">
            {(stats?.scored_current || 0).toLocaleString()} von {(stats?.total || 0).toLocaleString()} Firmen bewertet ({pct}%)
            {stats?.scored_stale > 0 && <span className="text-amber-400"> · {stats.scored_stale.toLocaleString()} mit älterer These — der Scan aktualisiert sie</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isAdmin ? (
            running || !stats ? null
              : stats.scored_current >= stats.total
                ? <span className="text-xs text-emerald-400">Alle Firmen bewertet ✓</span>
                : null
          ) : running ? (
            <button onClick={onPause} data-testid="thesis-scan-pause-btn"
              className="text-xs h-8 px-3 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors inline-flex items-center gap-1.5">
              <Pause className="w-3 h-3" /> Pausieren
            </button>
          ) : (stats && stats.scored_current < stats.total) ? (
            <button onClick={onStart} disabled={starting} data-testid="thesis-scan-start-btn"
              className="text-xs h-8 px-3 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
              {starting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              {stats.scored_current > 0 ? 'Scan fortsetzen' : 'Scan starten'}
            </button>
          ) : (
            <span className="text-xs text-emerald-400" data-testid="thesis-scan-complete">Alle Firmen bewertet ✓</span>
          )}
        </div>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full transition-all duration-700 ${running ? 'bg-emerald-500 animate-pulse' : 'bg-emerald-600'}`} style={{ width: `${pct}%` }} />
      </div>
      {running && (
        <p className="text-[11px] text-emerald-300 flex items-center gap-1.5" data-testid="thesis-scan-live">
          <Loader2 className="w-3 h-3 animate-spin" />
          Scan läuft — beste Kandidaten (NACE-vorpriorisiert) werden zuerst bewertet. Du kannst schon jetzt Vorschläge ziehen.
        </p>
      )}
      {run?.status === 'interrupted' && (
        <p className="text-[11px] text-amber-400">Scan wurde unterbrochen — „Scan fortsetzen" macht genau da weiter, wo er aufgehört hat.</p>
      )}
      {run?.status === 'failed' && <p className="text-[11px] text-red-400">Letzter Scan fehlgeschlagen — bitte erneut starten.</p>}
    </div>
  );
}

function ThesisCard({ cfg, isAdmin, onSaved }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.updateThesisConfig(text);
      toast.success('These aktualisiert — bestehende Bewertungen gelten als veraltet, bitte Scan neu starten');
      setEditing(false);
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };
  return (
    <div className="border border-violet-500/25 bg-violet-500/[0.04] rounded-sm" data-testid="thesis-config-card">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-2.5" data-testid="thesis-config-toggle">
        <span className="text-[10px] uppercase tracking-widest font-bold text-violet-300/80 flex items-center gap-1.5">
          <Target className="w-3 h-3" /> Die These — das sucht OLTA
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-violet-300/70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {editing ? (
            <>
              <textarea value={text} onChange={e => setText(e.target.value)} rows={16} data-testid="thesis-edit-textarea"
                className="w-full bg-secondary border border-border rounded-sm p-3 text-xs text-foreground leading-relaxed focus:outline-none focus:border-violet-500/50" />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setEditing(false)} className="text-xs h-8 px-3">Abbrechen</Button>
                <Button onClick={save} disabled={saving} data-testid="thesis-save-btn"
                  className="bg-violet-700 hover:bg-violet-600 text-white rounded-sm text-xs h-8 px-3">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'These speichern'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <pre className="text-[11px] text-foreground/85 leading-relaxed whitespace-pre-wrap font-sans" data-testid="thesis-text">{cfg?.text}</pre>
              {isAdmin && (
                <button onClick={() => { setText(cfg?.text || ''); setEditing(true); }} data-testid="thesis-edit-btn"
                  className="inline-flex items-center gap-1 text-[11px] text-violet-300 border border-violet-500/40 hover:bg-violet-500/10 rounded-sm px-2 py-1 transition-colors">
                  <Pencil className="w-3 h-3" /> These schärfen
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RefinementsCard({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.thesisRefinements();
      setItems(r.data.refinements || []);
      const running = (r.data.refinements || []).some(x => x.status === 'running');
      if (running && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          const rr = await api.thesisRefinements().catch(() => null);
          if (!rr) return;
          setItems(rr.data.refinements || []);
          if (!(rr.data.refinements || []).some(x => x.status === 'running')) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 5000);
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  const add = async () => {
    if (text.trim().length < 10) { toast.error('Bitte etwas ausführlicher beschreiben'); return; }
    setAdding(true);
    try {
      await api.thesisAddRefinement(text.trim());
      setText('');
      toast.success('Verschärfung angelegt — die KI prüft jetzt die Top-Treffer im Hintergrund');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen'); }
    setAdding(false);
  };

  const toggle = async (it) => {
    try {
      await api.thesisToggleRefinement(it.id, !it.active);
      setItems(prev => prev.map(x => (x.id === it.id ? { ...x, active: !it.active } : x)));
      toast.success(!it.active
        ? 'Verschärfung aktiv — wirkt ab dem nächsten Batch'
        : 'Verschärfung deaktiviert — ausgeblendete Firmen kommen ab dem nächsten Batch zurück');
    } catch { toast.error('Umschalten fehlgeschlagen'); }
  };

  const rerun = async (it) => {
    try {
      await api.thesisRerunRefinement(it.id);
      toast.success('Neu-Auswertung gestartet');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Start fehlgeschlagen'); }
  };

  const remove = async (it) => {
    try {
      await api.thesisDeleteRefinement(it.id);
      setItems(prev => prev.filter(x => x.id !== it.id));
      toast.success('Verschärfung gelöscht — alle Ausblendungen aufgehoben');
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="thesis-refinements-card">
      <div>
        <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300/80 flex items-center gap-1.5">
          <FilterX className="w-3 h-3" /> Verschärfungen — reversible Filter
        </p>
        <p className="text-[11px] text-muted-foreground mt-1">
          Beschreibe, was ihr NICHT (mehr) sucht oder wie etwas gemeint ist — die KI blendet betroffene Top-Treffer aus.
          Jede Verschärfung lässt sich jederzeit deaktivieren oder löschen; die Basis-Bewertung bleibt unangetastet.
        </p>
      </div>
      {isAdmin && (
        <div className="flex gap-2">
          <input value={text} onChange={e => setText(e.target.value)} maxLength={600}
            onKeyDown={e => { if (e.key === 'Enter') add(); }}
            placeholder={'z. B. „Keine reinen Tiefbau-Firmen ohne Technik-Fokus" oder „Mit Wartung meinen wir technische Instandhaltung, keine Reinigung"'}
            data-testid="refinement-input"
            className="flex-1 bg-secondary border border-border rounded-sm px-3 h-9 text-xs text-foreground focus:outline-none focus:border-amber-500/50" />
          <button onClick={add} disabled={adding} data-testid="refinement-add-btn"
            className="text-xs h-9 px-3 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60 shrink-0">
            {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Hinzufügen
          </button>
        </div>
      )}
      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map(it => (
            <div key={it.id} data-testid={`refinement-${it.id}`}
              className={`flex items-center gap-3 border rounded-sm px-3 py-2 ${it.active ? 'border-amber-500/30 bg-amber-500/[0.04]' : 'border-border opacity-60'}`}>
              <Switch checked={it.active} onCheckedChange={() => isAdmin && toggle(it)} disabled={!isAdmin} data-testid={`refinement-toggle-${it.id}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground leading-snug">{it.text}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5" data-testid={`refinement-status-${it.id}`}>
                  {it.status === 'running'
                    ? <span className="text-amber-300">Auswertung läuft… {it.evaluated || 0}/{it.total || '…'}</span>
                    : it.status === 'done'
                      ? <>blendet <span className="text-amber-300 font-medium">{(it.hits || 0).toLocaleString()}</span> von {(it.total || 0).toLocaleString()} Top-Treffern aus{it.failed ? ` · ${it.failed} nicht auswertbar` : ''}</>
                      : it.status === 'interrupted'
                        ? <span className="text-amber-400">Auswertung unterbrochen — bitte neu auswerten</span>
                        : <span className="text-red-400">Auswertung fehlgeschlagen — bitte neu auswerten</span>}
                  {!it.active && ' · inaktiv'}
                </p>
              </div>
              {isAdmin && (
                <button onClick={() => rerun(it)} title="Neu auswerten (z. B. nachdem der Scan weitere Firmen bewertet hat)"
                  data-testid={`refinement-rerun-${it.id}`} disabled={it.status === 'running'}
                  className="w-7 h-7 rounded-sm border border-border text-muted-foreground/50 hover:text-foreground transition-colors inline-flex items-center justify-center disabled:opacity-40">
                  <RotateCw className="w-3.5 h-3.5" />
                </button>
              )}
              {isAdmin && (
                <button onClick={() => remove(it)} title="Löschen — hebt alle Ausblendungen dieser Verschärfung auf"
                  data-testid={`refinement-delete-${it.id}`}
                  className="w-7 h-7 rounded-sm border border-border text-muted-foreground/50 hover:text-red-400 hover:border-red-500/40 transition-colors inline-flex items-center justify-center">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ThesisScreeningPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [run, setRun] = useState(null);
  const [rows, setRows] = useState([]);
  const [batch, setBatch] = useState(null);
  const [seenCount, setSeenCount] = useState(0);
  const [includeSeen, setIncludeSeen] = useState(false);
  const [onlyNew, setOnlyNew] = useState(true);
  const [filters, setFilters] = useState(() => {
    try { return { ...DEFAULT_FILTERS, ...(JSON.parse(sessionStorage.getItem(FILTER_KEY) || 'null') || {}) }; } catch { return DEFAULT_FILTERS; }
  });
  const [filtersOpen, setFiltersOpen] = useState(true);
  const { sectorConfig, ownerOptions } = useFilterOptions();
  const updateFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const resetFilters = () => { setFilters(DEFAULT_FILTERS); try { sessionStorage.removeItem(FILTER_KEY); } catch { /* ignore */ } };
  useEffect(() => { try { sessionStorage.setItem(FILTER_KEY, JSON.stringify(filters)); } catch { /* ignore */ } }, [filters]);
  const activeFilters = filterSummary(filters, ownerOptions);
  const [postRevMin, setPostRevMin] = useState('');
  const [postRevMax, setPostRevMax] = useState('');
  const [postSsMin, setPostSsMin] = useState('');
  const [postSsMax, setPostSsMax] = useState('');
  const [creating, setCreating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [viewedSet, setViewedSet] = useState(() => new Set());
  const [passTarget, setPassTarget] = useState(null);
  const [passText, setPassText] = useState('');
  const [passTags, setPassTags] = useState([]);
  const [passBench, setPassBench] = useState(false);
  const [passing, setPassing] = useState(false);
  const [bulkRun, setBulkRun] = useState(null);
  const bulkPollRef = useRef(null);
  const [simUrl, setSimUrl] = useState('');
  const [simRun, setSimRun] = useState(null);
  const [simRows, setSimRows] = useState([]);
  const [simStarting, setSimStarting] = useState(false);
  const simPollRef = useRef(null);
  const [bmSet, setBmSet] = useState(() => new Set());
  const pollRef = useRef(null);

  const poll = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.thesisScanStatus();
        setRun(r.data.run); setStats(r.data.stats);
        if (r.data.run?.status !== 'running') {
          clearInterval(pollRef.current);
          if (r.data.run?.status === 'done') toast.success('Thesis-Scan abgeschlossen — alle Firmen bewertet');
        }
      } catch { /* keep polling */ }
    }, 5000);
  }, []);

  const reloadBatch = useCallback(async () => {
    try {
      const r = await api.thesisLatestBatch();
      setBatch(r.data.batch); setRows(r.data.rows || []); setSeenCount(r.data.seen_count || 0);
    } catch { /* noop */ }
  }, []);

  const pollBulk = useCallback(() => {
    clearInterval(bulkPollRef.current);
    bulkPollRef.current = setInterval(async () => {
      try {
        const r = await api.bulkQuickScoreStatus();
        setBulkRun(r.data.run);
        if (r.data.run?.status !== 'running') {
          clearInterval(bulkPollRef.current);
          if (r.data.run?.status === 'done') {
            toast.success(`KI-Nachschärfung fertig — ${r.data.run.updated || 0} Firmen neu bewertet${r.data.run.failed ? `, ${r.data.run.failed} fehlgeschlagen` : ''}`);
            reloadBatch();
          } else if (r.data.run?.status === 'interrupted' || r.data.run?.status === 'failed') {
            toast.error('KI-Nachschärfung abgebrochen — bitte erneut starten');
          }
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, [reloadBatch]);

  const startBatchQuickScore = async () => {
    try {
      const r = await api.bulkQuickScore({ project_ids: rows.map(x => x.id) });
      setBulkRun(r.data);
      toast.success(`KI-Nachschärfung gestartet — ${r.data.total} Firmen${r.data.skipped ? ` (${r.data.skipped} bereits KI-bewertet übersprungen)` : ''}`);
      pollBulk();
    } catch (e) { toast.error(e.response?.data?.detail || 'Start fehlgeschlagen'); }
  };

  const pollSimilar = useCallback(() => {
    clearInterval(simPollRef.current);
    simPollRef.current = setInterval(async () => {
      try {
        const r = await api.thesisSimilarLatest();
        setSimRun(r.data.run);
        if (r.data.run?.status !== 'running') {
          clearInterval(simPollRef.current);
          setSimRows(r.data.rows || []);
          if (r.data.run?.status === 'done') {
            toast.success(`${(r.data.rows || []).length} ähnliche Firmen gefunden`);
          } else if (r.data.run?.status === 'failed') {
            toast.error(r.data.run?.error || 'Ähnlichkeits-Suche fehlgeschlagen');
          }
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, []);

  const loadBookmarks = useCallback(async () => {
    try {
      const r = await api.getBookmarks();
      setBmSet(new Set(r.data?.project_ids || []));
    } catch { /* noop */ }
  }, []);

  const toggleBookmark = async (e, r) => {
    e.stopPropagation();
    try {
      const res = await api.toggleBookmark(r.id);
      setBmSet(new Set(res.data?.project_ids || []));
      toast.success(res.data?.action === 'added' ? `${r.name} auf deine Merkliste gesetzt` : 'Von der Merkliste entfernt');
    } catch { toast.error('Bookmarken fehlgeschlagen'); }
  };

  const dismissSimilar = async () => {
    try {
      await api.thesisSimilarDismiss();
      setSimRun(null); setSimRows([]); setSimUrl('');
      toast.success('Vergleich geschlossen — nur noch die Batch-Vorschläge sichtbar');
    } catch { toast.error('Schließen fehlgeschlagen'); }
  };

  const startSimilar = async () => {
    if (!simUrl.trim()) { toast.error('Bitte eine Firmen-Website eingeben'); return; }
    setSimStarting(true);
    try {
      const r = await api.thesisSimilarStart(simUrl.trim(), buildExploreBody(filters));
      setSimRun(r.data); setSimRows([]);
      toast.success('Suche gestartet — Website wird gelesen und mit der Datenbank abgeglichen');
      pollSimilar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Start fehlgeschlagen'); }
    setSimStarting(false);
  };



  useEffect(() => {
    api.getThesisConfig().then(r => { setCfg(r.data); setStats(r.data.stats); }).catch(() => {});
    api.thesisScanStatus().then(r => {
      setRun(r.data.run); setStats(r.data.stats);
      if (r.data.run?.status === 'running') poll();
    }).catch(() => {});
    api.thesisLatestBatch().then(r => {
      setBatch(r.data.batch); setRows(r.data.rows || []); setSeenCount(r.data.seen_count || 0);
    }).catch(() => {});
    api.getViewedDeals().then(res => setViewedSet(new Set(res.data?.project_ids || []))).catch(() => {});
    api.bulkQuickScoreStatus().then(r => {
      if (r.data.run?.status === 'running') { setBulkRun(r.data.run); pollBulk(); }
    }).catch(() => {});
    api.thesisSimilarLatest().then(r => {
      setSimRun(r.data.run); setSimRows(r.data.rows || []);
      if (r.data.run) setSimUrl(r.data.run.url || '');
      if (r.data.run?.status === 'running') pollSimilar();
    }).catch(() => {});
    loadBookmarks();
    return () => { clearInterval(pollRef.current); clearInterval(bulkPollRef.current); clearInterval(simPollRef.current); };
    // eslint-disable-next-line
  }, []);

  const startScan = async () => {
    setStarting(true);
    try {
      const r = await api.thesisScanStart();
      setRun(r.data);
      toast.success(`Thesis-Scan gestartet — ${(r.data.total || 0).toLocaleString()} Firmen werden bewertet`);
      poll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Start fehlgeschlagen'); }
    setStarting(false);
  };

  const pauseScan = async () => {
    try {
      await api.thesisScanPause();
      setRun(prev => prev ? { ...prev, status: 'paused' } : prev);
      clearInterval(pollRef.current);
      toast.success('Scan pausiert — „Fortsetzen" macht genau da weiter');
    } catch { toast.error('Pausieren fehlgeschlagen'); }
  };

  const createBatch = async (size) => {
    setCreating(true);
    try {
      const r = await api.thesisCreateBatch({ size, include_seen: includeSeen, only_new: onlyNew, ...buildExploreBody(filters) });
      setBatch(r.data.batch); setRows(r.data.rows || []); setSeenCount(r.data.seen_count || 0);
      toast.success(`${(r.data.rows || []).length} neue Vorschläge — sortiert nach Thesis-Fit`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Keine Vorschläge verfügbar'); }
    setCreating(false);
  };

  const openDeal = (id, extra) => {
    setViewedSet(prev => new Set(prev).add(id));
    navigate(`/projects/${id}`, { state: { from: '/thesis-screening', ...(extra || {}) } });
  };

  const toggleFlag = async (e, r) => {
    e.stopPropagation();
    try {
      await api.setAcFlag(r.id, !r.ac_flagged);
      const upd = (list) => list.map(x => (x.id === r.id ? { ...x, ac_flagged: !r.ac_flagged } : x));
      setRows(upd);
      setSimRows(upd);
      toast.success(!r.ac_flagged ? `${r.name} gemerkt (!)` : 'Markierung entfernt');
    } catch { toast.error('Markieren fehlgeschlagen'); }
  };

  const submitPass = async () => {
    const reason = [passReasonText(passTags), passText.trim()].filter(Boolean).join('; ');
    if (!reason) { toast.error('Bitte Grund wählen oder eintippen'); return; }
    setPassing(true);
    try {
      const res = await api.turnDownProject(passTarget.id, reason, passBench, 'explorer', passTags);
      if (res.data.status === 'passed') {
        const upd = (list) => list.map(x => (x.id === passTarget.id
          ? { ...x, pipeline_stage: 'passed', stage_label: 'Passed', benched: passBench } : x));
        setRows(upd);
        setSimRows(upd);
        toast.success(`${passTarget.name} passed`);
      } else {
        toast.success(res.data.message || 'Pass vorgeschlagen — wartet auf Bestätigung');
      }
      setPassTarget(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Pass fehlgeschlagen'); }
    setPassing(false);
  };

  const decidedCount = rows.filter(r => r.pipeline_stage === 'passed' || r.ac_flagged).length;

  // Post-Nachschärfen-Filter: Firmen außerhalb der Bänder fliegen aus der aktuellen Liste (Talu, 24.08.2026)
  const parseNum = (v) => {
    const n = parseFloat(String(v ?? '').replace(',', '.').replace(/\s/g, ''));
    return Number.isFinite(n) ? n : null;
  };
  const inBand = (val, min, max) => {
    if (min === '' && max === '') return true;
    const n = parseNum(val);
    if (n === null) return false;
    if (min !== '' && n < parseFloat(min)) return false;
    if (max !== '' && n > parseFloat(max)) return false;
    return true;
  };
  const postFilterActive = postRevMin !== '' || postRevMax !== '' || postSsMin !== '' || postSsMax !== '';
  const displayRows = rows.filter(r =>
    inBand(r.revenue, postRevMin, postRevMax) && inBand(r.succession_score, postSsMin, postSsMax));
  const hiddenByPostFilter = rows.length - displayRows.length;
  const resetPostFilter = () => { setPostRevMin(''); setPostRevMax(''); setPostSsMin(''); setPostSsMax(''); };

  return (
    <div className="space-y-4" data-testid="thesis-screening-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl text-foreground tracking-tight flex items-center gap-2">
            <Target className="w-6 h-6 text-violet-400" /> KI Deal Screening
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Erst filtern, dann screenen: die besten Thesis-Treffer aus dem gefilterten Universum — in Batches durchklicken, merken oder aussortieren
          </p>
        </div>
        <button onClick={() => navigate('/bookmarks')} data-testid="bookmarks-toggle-btn"
          title={'Deine persönliche Merkliste öffnen (unabhängig vom AC-„!")'}
          className="text-xs h-8 px-3 rounded-sm border border-violet-500/40 text-violet-300 hover:bg-violet-500/10 transition-colors inline-flex items-center gap-1.5">
          <Bookmark className="w-3.5 h-3.5" />
          Merkliste ({bmSet.size})
        </button>
      </div>

      <ThesisCard cfg={cfg} isAdmin={isAdmin} onSaved={(d) => { setCfg(prev => ({ ...prev, ...d })); setStats(d.stats); }} />
      {isAdmin && <RefinementsCard isAdmin={isAdmin} />}
      <ScanCard run={run} stats={stats} onStart={startScan} onPause={pauseScan} starting={starting} isAdmin={isAdmin} />

      {/* Schritt 1: Filter — identisch zum Deal Explorer, gilt für Batches UND die Ähnlichkeits-Suche */}
      <div className="space-y-2" data-testid="thesis-filter-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <button onClick={() => setFiltersOpen(!filtersOpen)} data-testid="thesis-filters-toggle"
            className="text-[10px] uppercase tracking-widest font-bold text-violet-300/80 inline-flex items-center gap-1.5">
            <SlidersHorizontal className="w-3 h-3" /> Schritt 1 · Filter (wie im Deal Explorer)
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
            {activeFilters.length > 0 && (
              <span className="ml-1 normal-case tracking-normal font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-violet-500/15 border border-violet-500/40 text-violet-200" data-testid="thesis-filter-count">
                {activeFilters.length} aktiv
              </span>
            )}
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-muted-foreground/60">Gilt für „Nächste Vorschläge" und „Ähnliche Firmen finden"</span>
            {activeFilters.length > 0 && (
              <button onClick={resetFilters} data-testid="thesis-filters-reset"
                className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Zurücksetzen
              </button>
            )}
          </div>
        </div>
        {filtersOpen && (
          <DealFilterPanel filters={filters} updateFilter={updateFilter} setFilters={setFilters}
            sectorConfig={sectorConfig} ownerOptions={ownerOptions} accent="violet"
            searchPlaceholder="Name, Code-Name, Beschreibung, Produkte… (gilt für den nächsten Batch)" />
        )}
        {!filtersOpen && activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid="thesis-filter-chips">
            {activeFilters.map(c => (
              <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-violet-500/10 border border-violet-500/30 text-violet-200">{c}</span>
            ))}
          </div>
        )}
      </div>

      {/* Schritt 2: Batch controls */}
      <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="thesis-batch-controls">
        <p className="text-[10px] uppercase tracking-widest font-bold text-violet-300/80">Schritt 2 · Screening — Vorschläge nach Thesis-Fit</p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Nächste Vorschläge ziehen:</span>
          {[10, 50, 100].map(n => (
            <button key={n} onClick={() => createBatch(n)} disabled={creating || !stats?.scored_current}
              data-testid={`thesis-batch-${n}-btn`}
              className="text-xs h-8 px-3.5 rounded-sm border border-violet-500/40 text-violet-300 hover:bg-violet-500/10 font-mono font-bold transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
              {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : null}{n}
            </button>
          ))}
          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer ml-2"
            title={'Nur Firmen, die noch nicht in unseren Prozess aufgenommen wurden (noch vor „Under OLTA Review")'}>
            <Checkbox checked={onlyNew} onCheckedChange={v => setOnlyNew(!!v)} data-testid="thesis-only-new" />
            Noch nicht im Prozess
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
            <Checkbox checked={includeSeen} onCheckedChange={v => setIncludeSeen(!!v)} data-testid="thesis-include-seen" />
            Bereits gezeigte einbeziehen
          </label>
        </div>
        <span className="text-[11px] text-muted-foreground" data-testid="thesis-seen-count">
          {seenCount.toLocaleString()} Firmen bereits vorgeschlagen
        </span>
        </div>
        {activeFilters.length > 0 && (
          <p className="text-[10px] text-muted-foreground/60 pt-2 border-t border-border/40" data-testid="thesis-batch-filter-hint">
            Der nächste Batch berücksichtigt {activeFilters.length} Filter — Firmen ohne entsprechende Angabe werden ausgeblendet.
          </p>
        )}
      </div>

      {/* Ähnliche Firmen finden */}
      <div className="bg-card border border-border rounded-sm p-4 space-y-3" data-testid="thesis-similar-card">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-sky-300/80 flex items-center gap-1.5">
            <Globe className="w-3 h-3" /> Schritt 3 · Ähnliche Firmen finden
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Website einer Vorbild-Firma einfügen — die KI liest die Seite, versteht das Geschäft und findet die ähnlichsten Firmen in deiner Datenbank.
            {activeFilters.length > 0
              ? <span className="text-sky-300/80" data-testid="similar-filter-hint"> Gesucht wird nur innerhalb deiner {activeFilters.length} Filter aus Schritt 1.</span>
              : <span> Ohne Filter wird die gesamte Datenbank durchsucht.</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <input value={simUrl} onChange={e => setSimUrl(e.target.value)} maxLength={300}
            onKeyDown={e => { if (e.key === 'Enter') startSimilar(); }}
            placeholder="https://beispiel-firma.de" data-testid="similar-url-input"
            className="flex-1 bg-secondary border border-border rounded-sm px-3 h-9 text-xs text-foreground font-mono focus:outline-none focus:border-sky-500/50" />
          <button onClick={startSimilar} disabled={simStarting || simRun?.status === 'running'} data-testid="similar-start-btn"
            className="text-xs h-9 px-3 rounded-sm border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60 shrink-0">
            {(simStarting || simRun?.status === 'running') ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Ähnliche finden
          </button>
        </div>
        {simRun?.status === 'running' && (
          <p className="text-[11px] text-sky-300 flex items-center gap-1.5" data-testid="similar-status">
            <Loader2 className="w-3 h-3 animate-spin" />
            {simRun.phase === 'fetching' ? 'Website wird gelesen…'
              : simRun.phase === 'profiling' ? 'Geschäftsmodell wird analysiert…'
              : `Kandidaten werden bewertet… ${simRun.scored || 0}/${simRun.total_candidates || '…'}`}
          </p>
        )}
        {simRun?.status === 'failed' && (
          <p className="text-[11px] text-red-400" data-testid="similar-error">{simRun.error || 'Suche fehlgeschlagen — bitte erneut versuchen'}</p>
        )}
        {simRun?.status === 'done' && simRun.profile_summary && (
          <p className="text-[11px] text-muted-foreground italic leading-snug" data-testid="similar-profile">
            <span className="text-sky-300/80 not-italic font-medium">So versteht die KI die Firma: </span>{simRun.profile_summary}
          </p>
        )}
        {simRun && simRun.status !== 'running' && (
          <button onClick={dismissSimilar} data-testid="similar-dismiss-btn"
            className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2">
            Vergleich schließen & zurücksetzen
          </button>
        )}
      </div>

      {simRun?.status === 'done' && (
        <div className="bg-card border border-border rounded-sm" data-testid="similar-results-list">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-muted-foreground">
              <span className="text-foreground font-medium">{simRows.length} ähnliche Firmen</span> zu{' '}
              <span className="text-sky-300">{(simRun.url || '').replace(/^https?:\/\//, '')}</span> — sortiert nach Ähnlichkeit
              {filterSummary(simRun.filters).length > 0 && (
                <span className="text-violet-300/80" data-testid="similar-run-filters"> · Filter: {filterSummary(simRun.filters, ownerOptions).join(' · ')}</span>
              )}
              {simRun.pool_size != null && <span className="text-muted-foreground/60"> · {simRun.pool_size.toLocaleString()} Firmen im Pool</span>}
            </p>
            <button onClick={dismissSimilar} data-testid="similar-results-dismiss-btn"
              title="Vergleich schließen — Ergebnisliste ausblenden"
              className="text-muted-foreground/50 hover:text-foreground transition-colors inline-flex items-center gap-1 text-[11px]">
              <X className="w-3.5 h-3.5" /> Schließen
            </button>
          </div>
          {simRows.length === 0 && (
            <p className="px-4 py-6 text-sm text-muted-foreground text-center">Keine ausreichend ähnlichen Firmen in der Datenbank gefunden.</p>
          )}
          {simRows.map((r, i) => (
            <CompanyRow key={r.id} r={r} i={i} score={r.similarity_score} reason={r.similarity_reason}
              viewedSet={viewedSet} onOpen={openDeal} onFlag={toggleFlag}
              onPassClick={(row) => { setPassTarget(row); setPassText(''); setPassTags([]); setPassBench(false); }}
              bookmarkedSet={bmSet} onBookmark={toggleBookmark}
              prefix="similar" />
          ))}
        </div>
      )}

      {/* Schritt 4: Infrastructure Fund Screener — Fonds recherchieren, Profil bauen, Datenbank screenen */}
      <FundScreenerCard filters={buildExploreBody(filters)} activeFilters={activeFilters} ownerOptions={ownerOptions}
        rowProps={{ viewedSet, onOpen: openDeal, onFlag: toggleFlag, bookmarkedSet: bmSet, onBookmark: toggleBookmark,
          onPassClick: (row) => { setPassTarget(row); setPassText(''); setPassTags([]); setPassBench(false); } }} />

      {/* Batch list */}
      {batch && (
        <div className="bg-card border border-border rounded-sm" data-testid="thesis-batch-list">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-muted-foreground">
              Batch vom {new Date(batch.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} —
              <span className="text-foreground font-medium"> {rows.length} Vorschläge</span>, sortiert nach Thesis-Fit
              {filterSummary(batch.filters).length > 0 && (
                <span className="text-violet-300/80" data-testid="thesis-batch-filters">
                  {' · Filter: '}{filterSummary(batch.filters, ownerOptions).join(' · ')}
                </span>
              )}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              {isAdmin && (
              <button onClick={startBatchQuickScore} disabled={bulkRun?.status === 'running'}
                data-testid="thesis-batch-quickscore-btn"
                title="Claude bewertet Succession-Score, Infra-Fit und Sektor für alle Firmen in diesem Batch neu — bereits KI-bewertete werden übersprungen"
                className="text-[11px] h-7 px-2.5 rounded-sm border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
                {bulkRun?.status === 'running'
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> KI schärft nach… {bulkRun.processed || 0}/{bulkRun.total || 0}</>
                  : <><Sparkles className="w-3 h-3" /> Batch KI-nachschärfen</>}
              </button>
              )}
              <span className="text-[11px] text-muted-foreground">{decidedCount}/{rows.length} entschieden (gemerkt oder passed)</span>
            </div>
          </div>
          <div className="px-4 py-2 border-b border-border/60 bg-secondary/20 flex items-center gap-5 flex-wrap"
            data-testid="thesis-post-filter-row">
            <span className="text-[11px] text-muted-foreground">
              Ergebnisse filtern <span className="text-muted-foreground/50">(z. B. nach KI-Nachschärfung)</span>:
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">Umsatz (€m)</span>
              <NumInput value={postRevMin} onChange={setPostRevMin} ph="Min" testid="thesis-post-rev-min" />
              <span className="text-muted-foreground/40 text-xs">–</span>
              <NumInput value={postRevMax} onChange={setPostRevMax} ph="Max" testid="thesis-post-rev-max" />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">Succession-Score</span>
              <NumInput value={postSsMin} onChange={setPostSsMin} ph="Min" testid="thesis-post-ss-min" />
              <span className="text-muted-foreground/40 text-xs">–</span>
              <NumInput value={postSsMax} onChange={setPostSsMax} ph="Max" testid="thesis-post-ss-max" />
            </div>
            {postFilterActive && (
              <>
                <span className="text-[11px] text-amber-300" data-testid="thesis-post-filter-hidden">
                  {hiddenByPostFilter} von {rows.length} ausgeblendet (außerhalb der Bänder oder ohne Angabe)
                </span>
                <button onClick={resetPostFilter} data-testid="thesis-post-filter-reset"
                  className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2">
                  Zurücksetzen
                </button>
              </>
            )}
          </div>
          {displayRows.map((r, i) => (
            <CompanyRow key={r.id} r={r} i={i} score={r.thesis_score} reason={r.thesis_reason}
              viewedSet={viewedSet} onOpen={openDeal} onFlag={toggleFlag}
              onPassClick={(row) => { setPassTarget(row); setPassText(''); setPassTags([]); setPassBench(false); }}
              bookmarkedSet={bmSet} onBookmark={toggleBookmark}
              prefix="thesis" />
          ))}
        </div>
      )}

      {!batch && (
        <div className="text-center py-10 text-sm text-muted-foreground" data-testid="thesis-empty-state">
          {stats?.scored_current
            ? 'Noch kein Batch gezogen — Filter setzen (Schritt 1) und oben 10, 50 oder 100 Vorschläge ziehen.'
            : 'Noch keine Firmen bewertet — starte zuerst den Thesis-Scan.'}
        </div>
      )}

      <Dialog open={!!passTarget} onOpenChange={(o) => { if (!o) setPassTarget(null); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="thesis-pass-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" /> Pass — {passTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <PassReasonPicker selected={passTags} onToggle={(id) =>
            setPassTags(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id])} />
          <textarea value={passText} onChange={e => setPassText(e.target.value)} rows={2} maxLength={300}
            placeholder="Optionaler Zusatz zum Grund…" data-testid="thesis-pass-reason-input"
            className="w-full bg-secondary border border-border rounded-sm p-2 text-xs text-foreground resize-none focus:outline-none focus:border-red-500/50" />
          <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer">
            <Checkbox checked={passBench} onCheckedChange={v => setPassBench(!!v)} data-testid="thesis-pass-bench" />
            Benchen — bleibt im Explorer auffindbar (Passed, Benched)
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPassTarget(null)} className="text-xs h-8 px-3">Abbrechen</Button>
            <Button disabled={passing || (!passTags.length && !passText.trim())} data-testid="thesis-pass-confirm-btn"
              onClick={submitPass}
              className="bg-red-700 hover:bg-red-600 text-white rounded-sm font-medium text-xs h-8 px-3">
              {passing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Pass'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
